import type { ComponentChildren, JSX } from 'preact';
import { baseStyles, userSelectStyles } from './themes/VisStatusMessageStyles.css';
import type { UnpackSignals } from '@oracle/oraclejet-internal-utilities/attributeUtils';
import { Flex } from '#UNSAFE_Flex';
import { classNames } from '#utils/UNSAFE_classNames';
import { Size } from '#utils/UNSAFE_size';

export type Props = {
  /**
   * The placeholder content.
   */
  children: ComponentChildren;

  /**
   * The width of the component. The default value for this is theme driven.
   */
  width?: Size;

  /**
   * The height of the component. The default value for this is theme driven.
   */
  height?: Size;

  /**
   * Whether text in the placeholder content is selectable.
   */
  isSelectable?: boolean;

  /**
   * The component id.
   */
  id?: string;

  /**
   * The aria label of the component.
   */
  'aria-label'?: UnpackSignals<JSX.AriaAttributes>['aria-label'];

  /**
   * One or more ids (separated by spaces) of elements that describe the component. This will be used for the aria-describedby of the DOM element.
   */
  'aria-describedby'?: UnpackSignals<JSX.AriaAttributes>['aria-describedby'];

  /**
   * One or more ids (separated by spaces) of elements that label the component. This will be used for the aria-labelledby of the DOM element.
   */
  'aria-labelledby'?: UnpackSignals<JSX.AriaAttributes>['aria-labelledby'];
};

export function VisPlaceholder({
  children,
  isSelectable = false,
  width,
  height,
  ...otherProps
}: Props) {
  const itemClasses = classNames([!isSelectable && userSelectStyles, baseStyles]);

  return (
    <div
      class={itemClasses}
      style={{ width, height }}
      tabIndex={0}
      role="application"
      id={otherProps.id}
      aria-label={otherProps['aria-label']}
      aria-describedby={otherProps['aria-describedby']}
      aria-labelledby={otherProps['aria-labelledby']}>
      <Flex width="100%" height="100%" justify="center" align="center">
        {children}
      </Flex>
    </div>
  );
}
